import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCollection } from '../../hooks/useFirestore';
import { Service } from '../../types';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const Services: React.FC = () => {
  const { t } = useLanguage();
  const { data: allServices, loading } = useCollection<Service>('services');
  const services = allServices.filter(s => s.status !== 'archived');

  return (
    <section
      id="services"
      className="sw-section"
      role="region"
      aria-labelledby="services-title"
    >
      <div className="sw-container">
        <div className="section-header text-center">
          <span className="sw-eyebrow" aria-hidden="true">{t('services_eyebrow')}</span>
          <h2 id="services-title">{t('services_title')}</h2>
          <p className="section-desc">{t('services_desc')}</p>
        </div>

        <ul
          className="services-grid sw-grid"
          aria-label="Our services"
        >
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <li
                key={i}
                className="sw-card skeleton-card"
                aria-busy="true"
                aria-label="Loading service"
              >
                <div className="sw-skeleton" style={{ height: '180px', borderRadius: '12px', marginBottom: '20px' }} aria-hidden="true"></div>
                <div className="sw-skeleton" style={{ height: '24px', width: '60%', marginBottom: '10px' }} aria-hidden="true"></div>
                <div className="sw-skeleton" style={{ height: '16px', width: '80%' }} aria-hidden="true"></div>
              </li>
            ))
          ) : (
            services.map((service, i) => (
              <motion.li
                key={service.id}
                className="sw-card group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <div
                  className="service-icon-box"
                  aria-hidden="true"
                >
                  {service.icon || '⚡'}
                </div>
                <h3>{service.name}</h3>
                <p className="text-soft" style={{ marginTop: 'var(--sp-2)' }}>
                  {service.description}
                </p>
                <div className="flex items-center gap-2 mt-4 text-sm font-semibold" style={{ color: 'var(--primary)' }}>
                  <span>Learn more</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.li>
            ))
          )}
        </ul>
      </div>
    </section>
  );
};

export default Services;
